// Basisinformatie tile — profile-photo binary + write calls the generic Refine dataProvider can't make.
// The upload is multipart and the hero image is bytes, and the access token is bearer-only (in memory,
// not a cookie), so a plain <img src> can't carry it — the call attaches the bearer here and the hero
// goes through an authenticated blob fetch → object URL (mirrors src/api/media.ts).
import { getAccessToken } from "../auth";

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

/** An HTTP failure carrying the status code, so callers can branch on 400 (bad upload) / 404. */
export class AnimalPhotoApiError extends Error {
  readonly statusCode: number;
  constructor(statusCode: number, message?: string) {
    super(message ?? `HTTP ${statusCode}`);
    this.statusCode = statusCode;
    this.name = "AnimalPhotoApiError";
  }
}

function authHeaders(extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = { ...extra };
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

function base(animalId: string): string {
  return `${API_URL}/animals/${animalId}/photo`;
}

/** Upload the (already cropped/rotated) profile photo. Returns the refreshed animal detail JSON. */
export async function uploadAnimalPhoto(animalId: string, file: File): Promise<unknown> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(base(animalId), {
    method: "POST",
    headers: authHeaders(), // no Content-Type — the browser sets the multipart boundary
    body: form,
  });
  if (!res.ok) throw new AnimalPhotoApiError(res.status, res.statusText);
  return res.json();
}

/** Fetch the current profile photo's bytes (authenticated) as a Blob — for the hero image. */
/**
 * Klantportaal: daar draait de reserveringswizard van de backoffice ook, maar een klant haalt de foto van zijn dier
 * via het portaal. Leeg (de standaard) = de backoffice-route hieronder.
 */
let fotoBron: ((animalId: string) => Promise<Blob>) | null = null;
export function zetDierFotoBron(bron: ((animalId: string) => Promise<Blob>) | null): void {
  fotoBron = bron;
}

export async function fetchAnimalPhotoBlob(animalId: string): Promise<Blob> {
  if (fotoBron) return fotoBron(animalId);
  const res = await fetch(`${base(animalId)}/content`, { headers: authHeaders() });
  if (!res.ok) throw new AnimalPhotoApiError(res.status, res.statusText);
  return res.blob();
}

// ── AI photo-prefill ────────────────────────────────────────────────────────────────────────────
// The "AI-analyse" step of the Foto-wijzigen dialog: send the (already cropped/rotated) photo to the
// configured AI provider and get back proposed Basisinformatie fields, matched onto the shelter's own
// reference data. The apply itself is client-side — the dialog merges the operator's selection into the
// Basis edit form, which then saves through the normal PUT. Mirrors the Jmix openAiSuggestionsDialog.

/** One proposed field. `field` is the backend PrefillField name; `referenceId` is set for dropdown fields. */
export interface PhotoAiSuggestion {
  field: string;
  label: string;
  value: string;
  confidence: number;
  referenceId: number | null;
}

export interface PhotoAiAnalyzeResult {
  available: boolean;
  message: string;
  suggestions: PhotoAiSuggestion[];
}

/** Analyse the given (cropped) photo file and return field suggestions. Throws on HTTP failure. */
export async function analyzeAnimalPhoto(animalId: string, file: File): Promise<PhotoAiAnalyzeResult> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`${base(animalId)}/ai-analyze`, {
    method: "POST",
    headers: authHeaders(),
    body: form,
  });
  if (!res.ok) throw new AnimalPhotoApiError(res.status, res.statusText);
  return res.json() as Promise<PhotoAiAnalyzeResult>;
}

/** Whether an AI provider is configured — gates the "AI-analyse" button. Never throws (false on error). */
export async function fetchPhotoAiAvailability(animalId: string): Promise<boolean> {
  try {
    const res = await fetch(`${base(animalId)}/ai-availability`, { headers: authHeaders() });
    if (!res.ok) return false;
    const body = (await res.json()) as { available?: boolean };
    return body.available === true;
  } catch {
    return false;
  }
}
